<template>
  <BaseModal
    :model-value="isOpen"
    :title="headline"
    max-width-class="max-w-lg"
    @close="closeModal"
  >
    <div
      class="bg-danger-100 dark:bg-danger-900/20 flex h-12 items-center justify-center mb-4 mx-auto rounded-full w-12"
    >
      <ExclamationTriangleIcon class="dark:text-danger-400 h-6 text-danger-600 w-6" />
    </div>

    <div
      v-if="errorDetails && errorDetails.length > 0"
      class="dark:text-content-level-400 mb-6 space-y-2 text-center text-content-level-500 text-sm"
    >
      <div
        v-for="(detail, index) in errorDetails"
        :key="index"
        :class="
          detail.type === 'primary'
            ? 'font-medium text-content-level-700 dark:text-content-level-300'
            : 'text-content-level-500 dark:text-content-level-400'
        "
      >
        {{ detail.text }}
      </div>
    </div>

    <FormProblemBanner
      v-if="incidentProblem !== null"
      :problem="incidentProblem"
    />

    <template #footer>
      <div class="flex justify-end w-full">
        <button
          type="button"
          class="btn btn-active btn-primary cursor-pointer text-content-inverse"
          @click="closeModal"
        >
          {{ $t('commerce.availabilityErrorModal.close') }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import FormProblemBanner from '~/design-system/molecules/FormProblemBanner.molecule.vue'
import type { FormProblemState } from '~/services/forms/FormProblemState'
import { hasText } from '~/utils/hasText'
import { formatStoreTime } from '~/utils/formatStoreTime'
import { isOrderType, LeadTimeType } from '~/enums'
import { OrderTypePresentation } from '~/presentation/OrderTypePresentation'

interface CurrentLeadTimeRequirements {
  lead_time_type: LeadTimeType
  lead_time_hours: number
  required_minutes: number
  day_before_cutoff_time: string | null
  multiple_days_notice: number | null
}

interface LegacyLeadTimeRequirements {
  type: string
  hours?: number
  cutoff_time?: string
}

interface ErrorData {
  store_id: number
  store_name: string
  order_type: string
  address?: string
  requested_date?: string
  requested_time?: string
  current_time?: string
  available: boolean
  reason: string
  distance?: number
  operating_hours?: {
    day: string
    is_open: boolean
    open_time: string | null
    close_time: string | null
  }
  lead_time_requirements?: CurrentLeadTimeRequirements | LegacyLeadTimeRequirements
}

interface Props {
  /** Whether the availability error modal is open. */
  isOpen: boolean
  /** Optional headline message for the error. */
  errorMessage?: string
  /** Structured availability error details. */
  errorData?: ErrorData | null
  /** Server request id of the failed availability check, shown once as the incident number. */
  requestId?: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const headline = computed(() =>
  hasText(props.errorMessage)
    ? props.errorMessage
    : t('commerce.availabilityErrorModal.unavailable')
)

const incidentProblem = computed<FormProblemState | null>(() =>
  typeof props.requestId === 'string'
    ? { fieldCount: 0, kind: 'failure', messages: [], requestId: props.requestId }
    : null
)

const closeModal = () => {
  emit('close')
}

// Computed property to generate error details based on reason
const errorDetails = computed(() => {
  if (!props.errorData) return null

  const { reason, operating_hours, current_time } = props.errorData

  switch (reason) {
    case 'outside_delivery_radius':
      return [{ text: `Your location is outside this store's delivery area`, type: 'primary' }]

    case 'store_closed':
    case 'outside_operating_hours':
      if (
        operating_hours &&
        operating_hours.open_time !== null &&
        operating_hours.close_time !== null
      ) {
        const openTime = formatStoreTime(operating_hours.open_time)
        const closeTime = formatStoreTime(operating_hours.close_time)
        const dayName = operating_hours.day.charAt(0).toUpperCase() + operating_hours.day.slice(1)

        return [{ text: `${dayName} hours: ${openTime} - ${closeTime}`, type: 'primary' }]
      }
      return []

    case 'service_not_offered': {
      const orderType = props.errorData?.order_type
      const serviceLabel = isOrderType(orderType) ? OrderTypePresentation[orderType].label : 'this'
      return [
        {
          text: `This store does not offer ${serviceLabel} service`,
          type: 'primary'
        }
      ]
    }

    case 'past_time':
      return [
        { text: 'Requested time must be in the future', type: 'primary' },
        ...(typeof current_time === 'string' && current_time.length > 0
          ? [
              {
                text: t('commerce.availabilityErrorModal.currentStoreTime', {
                  time: formatStoreTime(current_time)
                }),
                type: 'secondary'
              }
            ]
          : [])
      ]

    case 'insufficient_lead_time': {
      const leadTimeInfo = props.errorData?.lead_time_requirements
      if (leadTimeInfo) {
        if (
          'lead_time_type' in leadTimeInfo &&
          leadTimeInfo.lead_time_type === LeadTimeType.DAY_BEFORE &&
          typeof leadTimeInfo.day_before_cutoff_time === 'string'
        ) {
          const cutoffTime = leadTimeInfo.day_before_cutoff_time
          const formatTime = (time24h: string) => {
            const hour = parseInt(time24h.slice(0, 2))
            const minutes = time24h.slice(3)
            const ampm = hour >= 12 ? 'PM' : 'AM'
            let displayHour = hour
            if (hour === 0) {
              displayHour = 12
            } else if (hour > 12) {
              displayHour = hour - 12
            }
            return `${displayHour}:${minutes} ${ampm}`
          }

          return [
            {
              text: `Orders must be placed by ${formatTime(cutoffTime)} the day before your event`,
              type: 'primary'
            }
          ]
        } else if (
          'lead_time_type' in leadTimeInfo &&
          leadTimeInfo.lead_time_type === LeadTimeType.MULTIPLE_DAYS &&
          typeof leadTimeInfo.multiple_days_notice === 'number'
        ) {
          const daysNotice = leadTimeInfo.multiple_days_notice
          return [
            {
              text: `Orders must be placed at least ${daysNotice} days in advance`,
              type: 'primary'
            }
          ]
        } else if ('required_minutes' in leadTimeInfo && leadTimeInfo.required_minutes > 0) {
          return [
            {
              text: `Order must be placed at least ${leadTimeInfo.required_minutes} minutes in advance`,
              type: 'primary'
            }
          ]
        }
      }
      return [{ text: 'Insufficient lead time for this order', type: 'primary' }]
    }

    default:
      return []
  }
})
</script>
